'use client';

import Link from 'next/link';
import {
    Container,
    Typography,
    Box,
    Paper,
} from '@mui/material';
import Navigation from '../../components/Navigation';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import CookieOutlinedIcon from '@mui/icons-material/CookieOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

interface SectionProps {
    icon: React.ReactElement;
    title: string;
    children: React.ReactNode;
    id?: string;
}

function Section({ icon, title, children, id }: SectionProps) {
    return (
        <Box id={id} sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                {icon}
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                        fontWeight: 700,
                        color: 'var(--text-color)',
                        fontSize: '1.1rem',
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Box
                sx={{
                    color: 'var(--muted)',
                    fontSize: '0.925rem',
                    lineHeight: 1.75,
                    '& p': { margin: '0 0 12px 0' },
                    '& p:last-child': { marginBottom: 0 },
                    '& a': {
                        color: '#2563eb',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': { opacity: 0.85 },
                    },
                    'html.dark & a': {
                        color: '#93bbfc',
                    },
                    '& ul': {
                        margin: '8px 0',
                        paddingLeft: '24px',
                    },
                    '& li': {
                        marginBottom: '6px',
                    },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

const iconStyle = { color: 'var(--primary)', fontSize: '22px' };

export default function PrivacyPage() {
    return (
        <>
            <Navigation />

            <Container
                maxWidth="md"
                className="main-container"
                sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}
            >
                {/* Header */}
                <Box sx={{ mb: 5 }}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            color: 'var(--text-color)',
                            letterSpacing: '-0.025em',
                            mb: 1,
                        }}
                    >
                        Privacy Policy
                    </Typography>
                    <Typography
                        sx={{
                            color: 'var(--muted)',
                            fontSize: '0.925rem',
                            lineHeight: 1.6,
                        }}
                    >
                        Last updated: September 10, 2026
                    </Typography>
                </Box>

                {/* TL;DR Card */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 5,
                        borderRadius: '12px',
                        border: '1px solid var(--editor-border)',
                        backgroundColor: 'var(--surface)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <ShieldOutlinedIcon sx={{ color: 'var(--primary)', fontSize: '20px' }} />
                        <Typography
                            sx={{ fontWeight: 700, color: 'var(--text-color)', fontSize: '0.95rem' }}
                        >
                            TL;DR
                        </Typography>
                    </Box>
                    <Typography sx={{ color: 'var(--muted)', fontSize: '0.925rem', lineHeight: 1.7 }}>
                        JSON transformation and patching run in your browser; JSON Patch Online does not upload editor contents. The page still makes normal hosting requests and uses{' '}
                        <a
                            href="https://vercel.com/docs/analytics"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2563eb', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                        >
                            Vercel Web Analytics
                        </a>
                        , which is cookieless. If you create a share link, the editor state is encoded in that URL.
                    </Typography>
                </Paper>

                {/* Sections */}
                <Section
                    id="client-side"
                    icon={<VisibilityOffOutlinedIcon sx={iconStyle} />}
                    title="100% Client-Side Processing"
                >
                    <p>
                        All JSON transformation, diffing, and patching happens in your browser. JSON Patch Online does not send the editor contents to its application server.
                    </p>
                    <p>
                        If you choose Copy Share Link, the current JSON and patch are encoded in the URL. That URL can remain in browser history, be copied into messages, and be visible to anyone who receives it. Do not put secrets in a share URL. Closing the tab clears in-memory editor state, but it does not remove a URL from browser history or from places where it was shared.
                    </p>
                </Section>

                <Section
                    id="what-we-dont-collect"
                    icon={<VisibilityOffOutlinedIcon sx={iconStyle} />}
                    title="What We Don't Collect"
                >
                    <p>To be explicit, we do <strong>not</strong> collect:</p>
                    <ul>
                        <li>Your JSON input data or patch output</li>
                        <li>Names or email addresses through the editor</li>
                        <li>Browser fingerprints created by JSON Patch Online</li>
                        <li>Any form of cross-site tracking data</li>
                    </ul>
                </Section>

                <Section
                    id="analytics"
                    icon={<BarChartOutlinedIcon sx={iconStyle} />}
                    title="Analytics"
                >
                    <p>
                        We use{' '}
                        <a
                            href="https://vercel.com/docs/analytics"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Vercel Web Analytics
                        </a>{' '}
                        to understand basic, aggregate usage patterns (e.g. page views,
                        referrers, country-level geography). Here&apos;s what makes it different
                        from traditional analytics:
                    </p>
                    <ul>
                        <li><strong>No cookies</strong> — nothing is stored on your device</li>
                        <li><strong>No cross-site tracking</strong> — your activity is not followed across the web</li>
                        <li><strong>Aggregate reporting</strong> — JSON Patch Online uses the reports to understand page-level traffic, not editor contents</li>
                        <li><strong>No editor payloads</strong> — analytics requests do not include the JSON in the editors</li>
                    </ul>
                </Section>

                <Section
                    id="cookies"
                    icon={<CookieOutlinedIcon sx={iconStyle} />}
                    title="Cookies"
                >
                    <p>
                        JSON Patch Online does not set analytics cookies. Vercel Web Analytics is configured as a cookieless service.
                    </p>
                    <p>
                        Your theme preference (light/dark) is stored in{' '}
                        <code style={{
                            backgroundColor: 'var(--surface-hover)',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '0.85em',
                        }}>
                            localStorage
                        </code>
                        , which is a browser-local storage mechanism that is never transmitted
                        to any server.
                    </p>
                </Section>

                <Section
                    id="security"
                    icon={<LockOutlinedIcon sx={iconStyle} />}
                    title="Data Security"
                >
                    <p>
                        JSON transformation happens locally, which avoids uploading editor contents for processing. The site is served over HTTPS and its assets are delivered through Vercel. Share URLs are different: they contain encoded editor state and should be treated as sensitive whenever the source JSON is sensitive.
                    </p>
                </Section>

                <Section
                    id="third-party"
                    icon={<CloudOutlinedIcon sx={iconStyle} />}
                    title="Third-Party Services"
                >
                    <p>The only third-party services we use are:</p>
                    <ul>
                        <li>
                            <strong>Vercel</strong> — hosting and edge delivery (
                            <a href="https://vercel.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer">
                                Vercel Privacy Policy
                            </a>
                            )
                        </li>
                        <li>
                            <strong>Vercel Web Analytics</strong> — privacy-friendly, aggregate-only
                            page analytics (
                            <a href="https://vercel.com/docs/analytics/privacy-policy" target="_blank" rel="noopener noreferrer">
                                Analytics Privacy Policy
                            </a>
                            )
                        </li>
                    </ul>
                    <p>We do not use Google Analytics, Facebook Pixel, or any other tracking service.</p>
                </Section>

                <Section
                    id="changes"
                    icon={<UpdateOutlinedIcon sx={iconStyle} />}
                    title="Changes to This Policy"
                >
                    <p>
                        If we make meaningful changes to this policy, we&apos;ll update the
                        &quot;Last updated&quot; date at the top. Given the nature of this tool
                        (everything runs client-side), significant privacy changes are unlikely.
                    </p>
                </Section>

                <Section
                    id="contact"
                    icon={<EmailOutlinedIcon sx={iconStyle} />}
                    title="Contact"
                >
                    <p>
                        If you have any questions about this policy or how the site works, you
                        can reach us at{' '}
                        <a href="mailto:patchjson@gmail.com">
                            patchjson@gmail.com
                        </a>
                        .
                    </p>
                </Section>

                {/* Back link */}
                <Box sx={{ mt: 2, pt: 3, borderTop: '1px solid var(--editor-border)' }}>
                    <Link
                        href="/"
                        style={{
                            color: '#1d4ed8',
                            textDecoration: 'none',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                        }}
                    >
                        ← Back to JSON Patch Online
                    </Link>
                </Box>
            </Container>
        </>
    );
}
